import { useMemo } from 'react';
import { apiRootEndpoint } from '../config';
import { useFlexAssessmentContext } from '../context/context';
import { FlexAssessmentContextAction } from '../types/flex-assessment-context-action';
import { NotificationType } from '../types/notification-type';
import {
  AssessmentAnswer,
  AssessmentApiResponse,
  AssessmentQuestion,
  AssessmentResultApiResponse,
  ExistingAnswer,
} from '../types/types';
import { UserId } from '../types/user-management-api-response';
import {
  ApiRequestFailureError,
  ApiRequestPayloadError,
} from '../utils/errors';
import { fetcher } from '../utils/fetcher';
import { STRINGS } from '../utils/strings';

const USER_FLEX_ASSESSMENT_ID_KEY = 'userFlexAssessmentId';
const BASE_END_POINT = `${apiRootEndpoint}/v1/flex-assessments`;
const USER_ASSESSMENT_ID_FROM_LOCAL_STORAGE: string | null = (():
  | string
  | null => {
  try {
    return localStorage.getItem(USER_FLEX_ASSESSMENT_ID_KEY);
  } catch (err) {
    return null;
  }
})();

export default function useAssessmentApi(): Record<string, Function> {
  const { state, dispatch } = useFlexAssessmentContext();

  const setAssessmentIdInLocalStorage = (id: string): void => {
    try {
      localStorage.setItem(USER_FLEX_ASSESSMENT_ID_KEY, id);
    } catch (err) {
      dispatch({
        type: FlexAssessmentContextAction.setAssessmentId,
        payload: id,
      });
    }
  };

  const persistedAssessmentId = useMemo((): string => {
    if (state.assessmentId) {
      return state.assessmentId;
    }
    /* // Temporarily disabled until there's new spec for resuming assessment
    if (USER_ASSESSMENT_ID_FROM_LOCAL_STORAGE) {
      return USER_ASSESSMENT_ID_FROM_LOCAL_STORAGE;
    } */
    return '';
  }, [state.assessmentId]);

  const getAssessment = async (
    accessCode: string,
    userId?: UserId
  ): Promise<void | Error> => {
    if (userId) {
      return startAssessment(accessCode, userId);
    }
    if (persistedAssessmentId) {
      return resumeAssessment(accessCode, persistedAssessmentId);
    }
    return startAssessment(accessCode);
  };

  const startAssessment = async (
    accessCode: string,
    userId?: UserId
  ): Promise<void | Error> => {
    try {
      const res: AssessmentApiResponse = await fetcher<AssessmentApiResponse>(
        `${BASE_END_POINT}/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessCode,
            ...(userId && { userId }),
          }),
        }
      );

      if (!Boolean(res.data.assessmentId)) {
        throw new ApiRequestPayloadError('userAssessmentId');
      }

      if (!Boolean(res.data.questions) || res.data.questions.length === 0) {
        throw new ApiRequestPayloadError('assessment.questions');
      }

      const { assessmentId: id, questions, subCategoryMaxScores } = res.data;

      setAssessmentIdInLocalStorage(id);

      dispatch({
        type: FlexAssessmentContextAction.setAssessmentId,
        payload: id,
      });
      dispatch({
        type: FlexAssessmentContextAction.setQuestions,
        payload: questions,
      });
      dispatch({
        type: FlexAssessmentContextAction.setSubCategoryMaxScores,
        payload: subCategoryMaxScores,
      });
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
        },
      });
    }
  };

  const getAssessmentById = async (
    assessmentId: string
  ): Promise<AssessmentApiResponse | void> => {
    try {
      const res: AssessmentApiResponse = await fetcher<AssessmentApiResponse>(
        `${BASE_END_POINT}/${assessmentId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!Boolean(res.data.assessmentId)) {
        throw new ApiRequestPayloadError('userAssessmentId');
      }

      if (!Boolean(res.data.questions) || res.data.questions.length === 0) {
        throw new ApiRequestPayloadError('assessment.questions');
      }

      dispatch({
        type: FlexAssessmentContextAction.setAssessmentId,
        payload: res.data.assessmentId,
      });

      return res;
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
        },
      });
    }
  };

  const makeQuestionsAndAnswers = (
    id: string,
    questions: AssessmentQuestion[],
    existingAnswers: Record<string, ExistingAnswer>
  ) => {
    if (existingAnswers && Object.keys(existingAnswers).length > 0) {
      const questionSet = structuredClone(questions);
      questionSet.forEach((question: AssessmentQuestion): void => {
        const existingAnswer: ExistingAnswer = existingAnswers[question.id];
        if (existingAnswer) {
          question.answerOptions.forEach((answer: AssessmentAnswer): void => {
            answer.selected = answer.id === existingAnswer.id;
          });
        }
      });
      return questionSet;
    }
    return questions;
  };

  const resumeAssessment = async (
    accessCode: string,
    assessmentId: string = persistedAssessmentId
  ): Promise<void | Error> => {
    const res = await getAssessmentById(assessmentId);

    if (!res?.data || Boolean(res.data?.completionTimestamp)) {
      return startAssessment(accessCode);
    }

    const {
      assessmentId: userAssessmentId,
      questions,
      existingAnswers,
    } = res!.data;

    const payload = makeQuestionsAndAnswers(
      userAssessmentId,
      questions,
      existingAnswers
    );
    dispatch({
      type: FlexAssessmentContextAction.setQuestions,
      payload,
    });
  };

  const getAssessmentResults = async (
    assessmentId: string = persistedAssessmentId
  ): Promise<void | Error> => {
    try {
      const assessmentResult: AssessmentResultApiResponse =
        await fetcher<AssessmentResultApiResponse>(
          `${BASE_END_POINT}/${assessmentId}?includeTemplate=true`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

      dispatch({
        type: FlexAssessmentContextAction.setAssessmentResult,
        payload: assessmentResult.data,
      });
      dispatch({
        type: FlexAssessmentContextAction.setSubCategoryMaxScores,
        payload: assessmentResult.data.subCategoryMaxScores,
      });
      return;
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
        },
      });
    }
  };

  const trackSelection = async (
    questionId: string,
    answerOptionId: string
  ): Promise<void | Error> => {
    try {
      const endpoint = `${BASE_END_POINT}/${persistedAssessmentId}/answers`;
      const init = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questionId, answerOptionId }),
      };
      const req = await fetch(endpoint, init);

      if (req.status !== 204) {
        throw new ApiRequestFailureError(endpoint, { req, init });
      }
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
        },
      });
    }
  };

  const completeAssessment = async (): Promise<void | Error> => {
    try {
      const assessmentResult = await fetcher<AssessmentResultApiResponse>(
        `${BASE_END_POINT}/${persistedAssessmentId}/isComplete`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isComplete: true,
          }),
        }
      );

      dispatch({
        type: FlexAssessmentContextAction.setAssessmentResult,
        payload: assessmentResult.data,
      });
      return;
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
        },
      });
    }
  };

  const associateEmail = async (
    emailAddress: string,
    sendResultEmail: boolean = false
  ): Promise<void | Error> => {
    try {
      if (!persistedAssessmentId) {
        return;
      }
      await fetcher(`${BASE_END_POINT}/${persistedAssessmentId}/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddress,
          sendResultEmail,
        }),
      });
    } catch (error) {
      /*
      dispatch({
        type: FlexAssessmentContextActions.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: STRINGS.ERRORS.RESULTS_NOT_SAVED_RETRY,
        },
      });*/
    }
  };

  return {
    getAssessment,
    resumeAssessment,
    getAssessmentResults,
    trackSelection,
    completeAssessment,
    associateEmail,
  };
}
